/**
 * @velyn-custom BEGIN — vely-product-quickview.js
 * Mobile QV gallery: two images per page + existing Velyn pill pagination.
 * Also defers Quick View image promotion until the drawer is actually open,
 * so Core LazyLoadEventHover cannot blast hidden QV width=720 on PDP load.
 */
(function () {
  const DRAWER_SELECTOR = "quickview-drawer.vely-product-quickview";
  const GALLERY_SELECTOR = "grid-gallery";
  const SLIDE_GAP = 12;
  const SLIDES_PER_PAGE = 2;
  const MOBILE_MAX = 767;
  const CORE_PAGINATION_CLASSES = [
    "hidden-md",
    "bg-white",
    "blur",
    "shadow",
    "fs-small",
    "heading-color",
    "lh-normal",
    "rounded",
  ];

  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  function getSwiper(gallery) {
    return gallery?.swiper || null;
  }

  function isDrawerOpen(drawer) {
    return drawer instanceof Element && drawer.classList.contains("active");
  }

  function getDrawer(root) {
    if (!(root instanceof Element)) {
      return document.querySelector(DRAWER_SELECTOR);
    }
    return root.matches?.(DRAWER_SELECTOR)
      ? root
      : root.closest(DRAWER_SELECTOR);
  }

  /**
   * Remove Core .image-lazy-load so global LazyLoader / LazyLoadEventHover
   * ignore these imgs while the drawer is closed. Keep data-srcset + src.
   */
  function demoteQuickviewLazyImages(root) {
    const scope = root instanceof Element ? root : document.querySelector(DRAWER_SELECTOR);
    if (!scope) return;
    const drawer = getDrawer(scope);
    if (drawer && isDrawerOpen(drawer)) return;

    scope
      .querySelectorAll("img.image-lazy-load, img[data-vely-qv-deferred]")
      .forEach((img) => {
        if (img.getAttribute("srcset") && !img.getAttribute("data-srcset")) {
          img.setAttribute("data-srcset", img.getAttribute("srcset"));
          img.removeAttribute("srcset");
        }
        img.classList.remove("image-lazy-load", "lazy-loaded");
        img.classList.add("vely-qv-deferred");
        img.setAttribute("data-vely-qv-deferred", "");
      });
  }

  function revealLazyImages(root) {
    if (!(root instanceof Element)) return;
    root
      .querySelectorAll(
        "img.image-lazy-load, img.vely-qv-deferred, img[data-vely-qv-deferred], img[data-srcset]"
      )
      .forEach((img) => {
        const picture = img.closest("picture");
        if (picture) {
          picture.querySelectorAll("source[data-srcset]").forEach((source) => {
            source.setAttribute("srcset", source.dataset.srcset);
            source.removeAttribute("data-srcset");
          });
        }
        if (img.dataset.srcset) {
          img.setAttribute("srcset", img.dataset.srcset);
          img.removeAttribute("data-srcset");
        }
        img.loading = "eager";
        img.classList.remove("image-lazy-load", "vely-qv-deferred");
        img.removeAttribute("data-vely-qv-deferred");
        img.classList.add("lazy-loaded");
        const motion = img.closest("motion-effect");
        if (motion) {
          motion.classList.remove("loading");
          motion.classList.add("loaded");
        }
      });
  }

  function syncQuickviewImages(root) {
    const drawer = getDrawer(root);
    if (!drawer) return;
    if (isDrawerOpen(drawer)) {
      revealLazyImages(drawer);
    } else {
      demoteQuickviewLazyImages(drawer);
    }
  }

  function bindSlideEvents(swiper, gallery) {
    if (!swiper || swiper.__velyQvBound) return;
    swiper.__velyQvBound = true;
    const reveal = () => {
      const drawer = gallery.closest(DRAWER_SELECTOR);
      if (!isDrawerOpen(drawer)) return;
      revealLazyImages(gallery);
    };
    swiper.on("slideChange", reveal);
    swiper.on("slideChangeTransitionStart", reveal);
    swiper.on("slideChangeTransitionEnd", reveal);
  }

  function applySwiperParams(swiper, slideCount) {
    const showTwo = slideCount > 1;
    swiper.params.spaceBetween = SLIDE_GAP;
    swiper.params.slidesPerView = showTwo ? SLIDES_PER_PAGE : 1;
    swiper.params.slidesPerGroup = showTwo ? SLIDES_PER_PAGE : 1;
    swiper.params.slidesPerGroupAuto = false;
    swiper.params.loop = false;
    swiper.params.rewind = false;
    swiper.params.centeredSlides = false;
    swiper.params.watchOverflow = true;
    swiper.allowSlideNext = true;
    swiper.allowSlidePrev = true;
    if (typeof swiper.loopDestroy === "function") {
      try {
        swiper.loopDestroy();
      } catch (error) {
        /* already not looping */
      }
    }
    swiper.update();
  }

  function preparePagination(pagination) {
    if (!pagination) return;
    pagination.classList.add("vely-swiper-pagination-pill");
    pagination.classList.remove(...CORE_PAGINATION_CLASSES);
    window.VelySwiperPagination?.pinInFlow(pagination);
  }

  function enhanceGallery(gallery, attempt) {
    if (!(gallery instanceof Element)) return;
    const drawer = gallery.closest(DRAWER_SELECTOR);
    if (!drawer) return;
    if (!isDrawerOpen(drawer)) return;
    if (!isMobile() || !gallery.classList.contains("swiper")) return;

    const pagination = gallery.querySelector(".swiper-pagination");
    preparePagination(pagination);
    revealLazyImages(gallery);

    const swiper = getSwiper(gallery);
    if (!swiper) {
      if ((attempt || 0) > 12) return;
      window.requestAnimationFrame(() => enhanceGallery(gallery, (attempt || 0) + 1));
      return;
    }

    const slideCount =
      swiper.slides?.length || gallery.querySelectorAll(".swiper-slide").length;
    applySwiperParams(swiper, slideCount);
    bindSlideEvents(swiper, gallery);

    const hasPillBullets =
      pagination?.classList.contains("vely-swiper-pagination-pill") &&
      pagination.querySelector(".swiper-pagination-bullet");

    if (hasPillBullets) {
      window.VelySwiperPagination?.refresh(swiper, pagination);
      swiper.update();
      return;
    }

    if (!window.VelySwiperPagination) {
      if ((attempt || 0) > 20) {
        swiper.update();
        return;
      }
      window.setTimeout(() => enhanceGallery(gallery, (attempt || 0) + 1), 50);
      return;
    }

    if (pagination) {
      const config = window.VelySwiperPagination.getConfig(pagination);
      if (config) {
        swiper.params.pagination = {
          clickable: true,
          type: "bullets",
          el: pagination,
          bulletClass: config.bulletClass,
          bulletActiveClass: config.bulletActiveClass,
          renderBullet: config.renderBullet,
        };
        try {
          swiper.pagination?.destroy();
        } catch (error) {
          /* pagination may not be ready yet */
        }
        swiper.pagination.init();
        swiper.pagination.render();
        swiper.pagination.update();
        window.VelySwiperPagination.refresh(swiper, pagination);
      }
    }

    swiper.update();
  }

  function enhanceAll(root) {
    const scope = root instanceof Element ? root : document;
    const drawer = getDrawer(scope);
    if (drawer && !isDrawerOpen(drawer)) {
      demoteQuickviewLazyImages(drawer);
      return;
    }

    const galleries = scope.matches?.(GALLERY_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(GALLERY_SELECTOR));
    galleries.forEach(enhanceGallery);

    if (drawer) revealLazyImages(drawer);
  }

  window.velyDemoteQuickviewLazyImages = demoteQuickviewLazyImages;
  window.velyRevealQuickviewLazyImages = revealLazyImages;
  window.velySyncQuickviewImages = syncQuickviewImages;
  window.velyEnhanceQuickviewGallery = enhanceAll;

  function observeDrawer(drawer) {
    if (!drawer || drawer.__velyQvGalleryObserved) return;
    drawer.__velyQvGalleryObserved = true;
    let frame = 0;

    new MutationObserver(() => {
      if (!isDrawerOpen(drawer)) {
        demoteQuickviewLazyImages(drawer);
      }
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (isDrawerOpen(drawer)) {
          enhanceAll(drawer);
        } else {
          demoteQuickviewLazyImages(drawer);
        }
      });
    }).observe(drawer, {
      attributes: true,
      attributeFilter: ["class"],
      childList: true,
      subtree: true,
    });
  }

  function init() {
    document.querySelectorAll(DRAWER_SELECTOR).forEach((drawer) => {
      demoteQuickviewLazyImages(drawer);
      observeDrawer(drawer);
      if (isDrawerOpen(drawer)) {
        enhanceAll(drawer);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
/* @velyn-custom END — vely-product-quickview.js */
